/**
 * Границы условий сделки — те же, что проверяет мастер.
 *
 * Повторены на клиенте не ради удобства: без них человек соберёт оффер,
 * заплатит газ и получит отказ контракта без объяснения. Проверка здесь
 * даёт внятную причину до отправки.
 *
 * Значения обязаны совпадать с contracts/types.tolk. Расхождение проявится
 * как «приложение разрешило, а контракт отверг».
 */

import type { DealTerms, RefundTier } from './schedule.ts';

export const MAX_FEE_BPS = 500;
export const MAX_DEFERRALS_CAP = 3;
export const MAX_DEAL_LIFETIME = 400 * 86400;
export const MIN_PERIOD = 3 * 86400;
export const MAX_PAYMENTS = 52;
export const MIN_UPFRONT_PCT = 10;
export const MAX_UPFRONT_PCT = 50;
/**
 * Наименьший платёж, который сделка примет. §9.6
 *
 * В нанотонах: валюта у сделки одна и родная. Порог от бессмысленной пыли,
 * а не от бедности — платёж меньше собственной обработки это работа
 * контракта, оплаченная дороже, чем она стоит.
 *
 * Значение обязано совпадать с РАЗВЁРНУТЫМ кодом сделки: строже — запрещаем
 * лишнее, слабее — пропускаем платёж, который контракт не примет, а деньги
 * к тому моменту уже ушли с кошелька.
 */
export const MIN_PAYMENT = 50_000_000n;
/** Цена переноса — не больше четверти планового платежа. */
export const MAX_DEFER_FEE_PCT = 25;

/**
 * Сколько стоит перенос на деле — доля планового платежа.
 *
 * Не настройка продавца: ручек на форме и так шесть, а разброс этой цены
 * между офферами мешал бы покупателю их сравнивать. Живёт здесь, а не на
 * экране, потому что экранов, назначающих условия, два — конструктор и
 * встречное предложение, — и они уже разошлись: один брал полпроцента от
 * суммы сделки, другой пятую часть платежа. Разница в десять раз за одно
 * и то же действие, и оба числа были написаны в коде без объяснения.
 *
 * Считается от платежа, а не от цены: так же устроен потолок выше, и
 * выйти за него нельзя ни при каком сочетании цены, взноса и числа
 * платежей. Прежние полпроцента от суммы при длинном графике за потолок
 * выходили, и оффер переставал публиковаться вовсе.
 */
export const DEFER_FEE_PCT = 2;

/** Цена одного переноса по условиям сделки. */
export function deferFeeFor(totalPrice: bigint, upfront: bigint, nPayments: number): bigint {
  if (nPayments <= 0) return 0n;
  return (((totalPrice - upfront) / BigInt(nPayments)) * BigInt(DEFER_FEE_PCT)) / 100n;
}

export type TermsProblem =
  | 'price_zero'
  | 'bad_tier'
  | 'too_many_deferrals'
  | 'bad_payments_count'
  | 'period_too_short'
  | 'upfront_too_small'
  | 'upfront_too_large'
  | 'defer_fee_too_high'
  | 'deadline_too_far'
  | 'schedule_past_deadline'
  | 'upfront_below_min'
  | 'payment_below_min';

/**
 * Повторяет validateRequest из contracts/Master.tolk шаг в шаг.
 * Возвращает первую найденную причину — как и контракт, который бросает
 * на первой же непройденной проверке.
 */
export function validateTerms(terms: DealTerms, now: number): TermsProblem | null {
  if (terms.totalPrice <= 0n) return 'price_zero';
  if (terms.refundTier < 0 || terms.refundTier > 3) return 'bad_tier';
  if (terms.maxDeferrals > MAX_DEFERRALS_CAP) return 'too_many_deferrals';
  if (terms.nPayments < 1 || terms.nPayments > MAX_PAYMENTS) return 'bad_payments_count';
  if (terms.period < MIN_PERIOD) return 'period_too_short';

  if (terms.upfront * 100n < terms.totalPrice * BigInt(MIN_UPFRONT_PCT)) return 'upfront_too_small';
  if (terms.upfront * 100n > terms.totalPrice * BigInt(MAX_UPFRONT_PCT)) return 'upfront_too_large';

  // Каждый платёж по такой сделке должен быть в принципе оплачиваемым:
  // отказ придёт не при создании, а при переводе денег.
  if (terms.upfront < MIN_PAYMENT) return 'upfront_below_min';
  if ((terms.totalPrice - terms.upfront) / BigInt(terms.nPayments) < MIN_PAYMENT) {
    return 'payment_below_min';
  }

  // Умножаем на nPayments, чтобы не делить до сравнения — как в контракте.
  if (
    terms.deferFee * 100n * BigInt(terms.nPayments) >
    (terms.totalPrice - terms.upfront) * BigInt(MAX_DEFER_FEE_PCT)
  ) {
    return 'defer_fee_too_high';
  }

  if (terms.deadline - now > MAX_DEAL_LIFETIME) return 'deadline_too_far';

  const longest = (terms.nPayments + terms.maxDeferrals) * terms.period;
  if (now + longest > terms.deadline) return 'schedule_past_deadline';

  return null;
}

/** Что покупатель предлагает в запросе. Уровень возврата и переносы сюда
 *  не входят: их назначает продавец в своём ответе. */
export interface RequestTerms {
  totalPrice: bigint;
  upfront: bigint;
  period: number;
  nPayments: number;
}

export type RequestProblem =
  | 'price_zero'
  | 'bad_payments_count'
  | 'period_too_short'
  | 'upfront_out_of_range'
  | 'upfront_below_min'
  | 'payment_below_min';

/**
 * Границы запроса на рассрочку.
 *
 * Уже, чем у сделки: покупатель задаёт только цену, взнос и график. Коридор
 * взноса тот же, что у мастера, — иначе запрос нельзя было бы принять, не
 * переторговывая взнос заново.
 */
export function validateRequest(r: RequestTerms): RequestProblem | null {
  if (r.totalPrice <= 0n) return 'price_zero';
  if (r.nPayments < 1 || r.nPayments > MAX_PAYMENTS) return 'bad_payments_count';
  if (r.period < MIN_PERIOD) return 'period_too_short';
  if (
    r.upfront * 100n < r.totalPrice * BigInt(MIN_UPFRONT_PCT) ||
    r.upfront * 100n > r.totalPrice * BigInt(MAX_UPFRONT_PCT)
  ) {
    return 'upfront_out_of_range';
  }
  // Просить рассрочку, по которой нельзя заплатить, бессмысленно: продавец
  // примет запрос, создаст сделку и упрётся в отказ уже деньгами.
  if (r.upfront < MIN_PAYMENT) return 'upfront_below_min';
  if ((r.totalPrice - r.upfront) / BigInt(r.nPayments) < MIN_PAYMENT) return 'payment_below_min';
  return null;
}

/** Допустимые уровни возврата — перечисление, а не диапазон. */
export const REFUND_TIERS: readonly RefundTier[] = [0, 1, 2, 3];
